import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useCartStore } from '../../stores/cartStore';
import { AlertCircle, FileText, Gift, Info, Loader2, Ticket, Trash2 } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import clsx from 'clsx';
import { ModalePaiement } from '../../components/checkout/ModalePaiement';
import { useDevis } from '../../lib/orders/useDevis';
import { commander, ErreurDeCommande } from '../../lib/orders/gateway.supabase';
import { adresseDeConfirmation, ecritureDuChoix, sortieDeLaFenetre } from '../../lib/orders/tunnel';
import type { Commande } from '../../lib/orders/devis';
import { completudeProfil } from '../../lib/completude';
import { offreDePaiement, type OffreDePaiement } from '../../lib/paiement/stripe';
import { payerParStripe, ErreurDePaiement } from '../../lib/paiement/gateway.supabase';
import {
  SEUIL_REVENUS_CENTIMES,
  ajouterCode,
  estLocal,
  estReduit,
  euros,
  libelleTarif,
  lireRefusDeCode,
  normaliserCode,
  phraseDeReste,
  retirerCode,
  texteDeclaration,
} from '../../lib/orders/devis';

/*
  L'ecran de paiement, rebati sur `quote_order` et `create_order`.

  ## Ce qui a change, et pourquoi

  L'ancien ecran cochait « tarif reduit » et appelait `updatePriceType()`, qui
  recalculait les prix DANS LE PANIER a partir d'un `sessionStorage`. Pour le
  second enfant, la cle manquait et le prix restait le normal — 221
  inscriptions mal tarifees.

  Ici, cocher la case ne change rien localement : elle est envoyee a
  `quote_order`, qui rend les prix. L'ecran n'a plus de regle de tarif a
  connaitre, donc plus de regle a se tromper.

  ## Le seuil affiche

  L'ancien texte annoncait 2 070,48 EUR. Le seuil retenu est le RMMMG belge,
  2 233,61 EUR, et il vit dans `SEUIL_REVENUS_CENTIMES` — un seul endroit.
  Ce qui est MONTRE au parent est conserve avec sa declaration : le controle a
  posteriori a besoin de savoir a quoi il a repondu.

  ## Les codes cadeaux

  Cumulables, sans limite par enfant. Chaque code couvre la semaine eligible
  la moins chere non encore couverte — decide par la base, pas ici. L'ecran
  montre seulement quelle semaine a ete offerte, pour que le parent le voie
  avant de valider.

  ## Valider ouvre la fenetre ; c'est le CHOIX qui ecrit

  « Valider les inscriptions » appelait `create_order` puis ouvrait la fenetre
  de paiement. Un parent qui se ravisait devant elle etait deja inscrit, et
  rien ne le defaisait — voir le defaut du 10 septembre 2026, raconte au long
  dans `lib/orders/tunnel.ts`.

  Le bouton n'ecrit donc plus rien : il ouvre la fenetre, nourrie par le DEVIS
  (`quote_order`, qui est STABLE et ne prend aucun verrou). `create_order`
  s'appelle au clic sur l'un des deux modes de paiement, et une seule fois.
*/

const CheckoutPage = () => {
  /*
    Quel geste est en vol : `'stripe'`, `'virement'`, ou rien. Un booleen
    suffisait tant que « Valider » etait le seul appel ; depuis que les DEUX
    boutons de la fenetre ecrivent la commande, il faut savoir LEQUEL, sinon
    le bouton Bancontact annonce « Ouverture du paiement… » a un parent qui a
    choisi le virement.
  */
  const [enCours, setEnCours] = useState<'stripe' | 'virement' | null>(null);
  /*
    La commande est passee. Sans ce drapeau, l'ecran renvoyait au panier au
    moment meme ou il fallait confirmer : `clearCart()` vidait le panier, le
    rendu suivant voyait `items.length === 0`, et l'effet ci-dessous
    redirigeait vers `/cart` — le parent arrivait sur un panier vide, sans
    savoir si son enfant etait inscrit. C'est le defaut signale le 4 septembre.

    Le panier n'est d'ailleurs plus vide ici : c'est la page de confirmation
    qui s'en charge, une fois qu'elle est affichee.
  */
  const [commandePassee, setCommandePassee] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  /*
    La fenetre de paiement est-elle ouverte ? Un booleen, et non la presence
    d'une commande : elle s'ouvre justement AVANT qu'il y ait quoi que ce soit
    a montrer en base.
  */
  const [fenetreOuverte, setFenetreOuverte] = useState(false);
  /*
    Ce que la fenetre PROPOSE, fige a l'ouverture. Le devis continue de vivre
    derriere elle ; le laisser piloter la fenetre ouverte, c'est risquer qu'un
    devis momentanement illisible fasse afficher « Rien a payer » a quelqu'un
    qui doit 240 EUR. Le parent choisit sur ce qu'on lui a montre.
  */
  const [offre, setOffre] = useState<OffreDePaiement>({ forme: 'rien_a_payer' });
  /*
    La commande, telle que `create_order` l'a rendue — `null` tant que le
    parent n'a pas choisi. C'est la seule trace d'un commit dans cet ecran, et
    c'est elle qui empeche d'en faire un second.
  */
  const [commande, setCommande] = useState<Commande | null>(null);
  const [erreurPaiement, setErreurPaiement] = useState<string | null>(null);
  /*
    La commande EN VOL. Deux clics rapprochés — deux fois le même bouton, ou
    un sur chacun — verraient tous deux `commande` à `null` : un `setState` de
    React ne prend effet qu'au rendu suivant, et `disabled` non plus. Ils
    appelleraient donc `create_order` DEUX fois. La seconde buterait sur
    l'index `registrations_une_inscription_active` et afficherait au parent
    « Cet enfant est déjà inscrit à cette semaine » — en laissant une facture
    orpheline derrière elle.

    Une référence, elle, est à jour tout de suite. Le second clic attend la
    promesse du premier et repart avec la même commande.
  */
  const commandeEnVol = useRef<Promise<Commande | null> | null>(null);
  const [modaleDeclaration, setModaleDeclaration] = useState(false);
  const [declarationCochee, setDeclarationCochee] = useState(false);
  const [tarifReduit, setTarifReduit] = useState(false);
  const [codes, setCodes] = useState<string[]>([]);
  const [saisieCode, setSaisieCode] = useState('');

  const { user, profile, fetchProfile } = useAuthStore();
  const { items } = useCartStore();
  const navigate = useNavigate();

  const lignes = useMemo(
    () => items.map((i) => ({ session_id: i.activity_id, kid_id: i.kid_id })),
    [items],
  );

  const { devis, chargement, erreur: erreurDevis } = useDevis(
    user?.id, lignes, tarifReduit, codes,
  );

  /*
    Le devis refuse-t-il un CODE ? Si oui, l'ecran peut proposer de le retirer
    au lieu de laisser le parent devant un message.

    C'est le defaut signale le 4 septembre : « Quand le code n'existe pas le
    panier ne peut plus etre valide. » Le message etait juste, mais le
    recapitulatif disparaissait et rien ne disait quoi faire.
  */
  const refus = lireRefusDeCode(erreurDevis);

  useEffect(() => {
    if (items.length === 0 && !commandePassee) navigate('/cart');
    if (user && !profile) fetchProfile();
  }, [items.length, commandePassee, user, profile, navigate, fetchProfile]);

  /* De quoi retrouver le nom d'un enfant et d'un stage a partir d'une ligne du devis. */
  const parLigne = useMemo(() => {
    const m = new Map<string, typeof items[number]>();
    items.forEach((i) => m.set(`${i.activity_id}|${i.kid_id}`, i));
    return m;
  }, [items]);

  const basculerTarifReduit = () => {
    if (tarifReduit) {
      setTarifReduit(false);
      setDeclarationCochee(false);
      return;
    }
    setModaleDeclaration(true);
  };

  const confirmerDeclaration = () => {
    setTarifReduit(true);
    setModaleDeclaration(false);
  };

  /*
    Le garde-fou de saisie. Un meme code entre deux fois ne peut pas offrir
    deux semaines : `ajouterCode` le refuse ici (a la casse et aux espaces
    pres), `quote_order` le refuserait ensuite, et l'index partiel
    `gratuity_one_active_use` en dernier ressort.

    Trois barrieres, mais c'est celle-ci qui evite au parent un message
    d'erreur : le mieux est qu'il ne le voie jamais.
  */
  const soumettreCode = (e: React.FormEvent) => {
    e.preventDefault();
    const avant = codes.length;
    const apres = ajouterCode(codes, saisieCode);
    if (apres.length === avant && saisieCode.trim()) {
      setErreur(`Le code « ${normaliserCode(saisieCode)} » est déjà dans votre liste. `
        + "Un même code ne rend gratuite qu'une seule semaine.");
    } else {
      setErreur(null);
    }
    setCodes(apres);
    setSaisieCode('');
  };

  const valider = () => {
    /*
      La meme definition de « complet » que l'accueil, la fiche enfant et la
      page des stages — `lib/completude` — plutot qu'une liste de champs
      recopiee ici. C'etait la seule au depart, et elle arrivait trop tard : le
      parent avait choisi ses semaines, rempli son panier, et lisait ici qu'il
      lui manquait une adresse. Trois secondes de message, une redirection vers
      le profil, panier derriere lui.

      Le controle est desormais pose bien avant — l'accueil le dit en arrivant,
      la page des stages refuse de montrer les semaines. Celui-ci reste, mais
      comme dernier filet : il ne redirige plus de force, il NOMME ce qui
      manque et laisse le parent y aller quand il veut.
    */
    const etat = completudeProfil(profile);

    if (!etat.complet) {
      setErreur(
        etat.bloquants.length === 1
          ? `${etat.bloquants[0].libelle} Complétez votre profil pour continuer.`
          : `Il manque ${etat.bloquants.length} informations dans votre profil : `
            + etat.bloquants.map((m) => m.libelle.replace(' est manquant.', '')).join(', ')
            + '.',
      );
      return;
    }
    /*
      On ne laisse pas valider sur un devis absent : ce serait commander sans
      savoir combien. Le refus de la base serait le meme, mais il arriverait
      apres la confirmation.
    */
    if (!devis) {
      setErreur(erreurDevis ?? 'Le montant de la commande n\'a pas pu être calculé.');
      return;
    }
    /*
      Et c'est TOUT. Aucune ecriture : la fenetre s'ouvre sur le devis, et le
      parent peut encore en sortir sans laisser de trace.
    */
    setErreurPaiement(null);
    setOffre(offreDePaiement(devis));
    setFenetreOuverte(true);
  };

  /**
   * Ecrire la commande — ou reprendre celle qui existe deja.
   *
   * Appelee par les DEUX modes de paiement, au moment du clic. Rend `null` si
   * la base a refuse : la phrase est alors affichee dans la fenetre, et rien
   * n'a ete ecrit.
   */
  const passerCommande = (): Promise<Commande | null> => {
    /*
      Un second `create_order` apres un echec de Stripe creerait une seconde
      facture et buterait sur l'index d'unicite des inscriptions. On reprend.
    */
    const geste = ecritureDuChoix(commande);
    if (geste.forme === 'reprendre') return Promise.resolve(geste.commande);

    /* Un premier appel est deja parti : le second s'y raccroche. */
    if (commandeEnVol.current) return commandeEnVol.current;

    if (!user) {
      setErreurPaiement('Vous devez être connecté pour commander.');
      return Promise.resolve(null);
    }

    const enVol = (async (): Promise<Commande | null> => {
      try {
        /*
          Une seule transaction : facture, inscriptions, remises et provision,
          ou rien. `lignes` ne porte que des identifiants — aucun prix ne part
          d'ici.
        */
        const passee = await commander(
          user.id,
          lignes,
          tarifReduit,
          tarifReduit
            ? { threshold_cents: SEUIL_REVENUS_CENTIMES, statement_text: texteDeclaration() }
            : null,
          codes,
        );

        /*
          On ne vide PAS le panier ici. C'est la page de confirmation qui le
          fait, une fois affichee : vider avant de naviguer declenchait la
          redirection vers `/cart` et le parent ne voyait jamais sa
          confirmation.

          Le document et le courriel viennent APRES, sur la page de
          confirmation : produire un PDF et envoyer un message prennent
          quelques secondes, et l'inscription, elle, est deja acquise. Le
          parent doit le lire tout de suite.
        */
        setCommandePassee(true);
        setCommande(passee);
        return passee;
      } catch (e) {
        /*
          La place a pu partir entre l'ouverture de la fenetre et ce clic —
          c'est le prix assume de n'ecrire qu'au choix. `create_order` le dit
          deja en francais : « la semaine du … n'a plus assez de places ».

          On relache la reference : rien n'a ete ecrit, donc reessayer est
          legitime — le refus peut n'avoir ete qu'une coupure reseau.
        */
        commandeEnVol.current = null;
        setErreurPaiement(e instanceof ErreurDeCommande ? e.message : String(e));
        return null;
      }
    })();

    commandeEnVol.current = enVol;
    return enVol;
  };

  /*
    Commander, puis partir chez Stripe. Un echec de Stripe ne defait rien : la
    commande est passee, le parent reste sur la fenetre, inscrit, et le
    virement lui reste ouvert.
  */
  const payerMaintenant = async () => {
    if (enCours) return;
    setEnCours('stripe');
    setErreurPaiement(null);
    try {
      const passee = await passerCommande();
      if (!passee) {
        setEnCours(null);
        return;
      }
      await payerParStripe(passee.invoice_id, adresseDeConfirmation(passee));
      /*
        Pas de `setEnCours(false)` ici : la page est en train d'etre remplacee
        par celle de Stripe. Relacher le drapeau rendrait les boutons
        cliquables pendant la bascule, et un second clic ouvrirait une seconde
        session de paiement pour la meme facture.
      */
    } catch (e) {
      setErreurPaiement(e instanceof ErreurDePaiement ? e.message : String(e));
      setEnCours(null);
    }
  };

  /*
    Payer par virement : c'est ici que l'engagement des 15 jours est pris, donc
    c'est ici que la commande s'ecrit. Le drapeau reste leve si elle passe —
    on quitte l'ecran.
  */
  const payerPlusTard = async () => {
    if (enCours) return;
    setEnCours('virement');
    setErreurPaiement(null);
    const passee = await passerCommande();
    if (!passee) {
      setEnCours(null);
      return;
    }
    navigate(adresseDeConfirmation(passee));
  };

  /*
    La croix, Echap, le fond. Trois issues selon ce qui a deja ete ecrit —
    la regle est dans `tunnel.ts`, avec le defaut qu'elle empeche.
  */
  const fermerLaFenetre = () => {
    const sortie = sortieDeLaFenetre(commande, enCours !== null);
    if (sortie.forme === 'attendre') return;
    if (sortie.forme === 'confirmer') {
      navigate(sortie.adresse);
      return;
    }
    setFenetreOuverte(false);
    setErreurPaiement(null);
  };

  return (
    <div className="container max-w-5xl mx-auto py-12 px-4 animate-fade-in">
      <h1 className="text-3xl font-bold mb-8">Finaliser la commande</h1>

      <div className="grid md:grid-cols-2 gap-8">
        <div className="order-2 md:order-1">
          <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
            <div className="p-4 bg-gray-50 border-b">
              <h2 className="text-lg font-semibold">Informations de facturation</h2>
            </div>
            <div className="p-4">
              {profile ? (
                <div className="space-y-4">
                  <div>
                    <h3 className="font-medium text-gray-700 mb-1">Contact</h3>
                    <p className="text-gray-600">{profile.prenom} {profile.nom}</p>
                    <p className="text-gray-600">{user?.email}</p>
                    <p className="text-gray-600">{profile.telephone || 'Aucun numéro renseigné'}</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-700 mb-1">Adresse</h3>
                    {profile.adresse && profile.cpostal && profile.localite ? (
                      <>
                        <p className="text-gray-600">{profile.adresse}</p>
                        <p className="text-gray-600">{profile.cpostal} {profile.localite}</p>
                      </>
                    ) : (
                      <p className="text-gray-600">Aucune adresse renseignée</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="py-4 flex justify-center">
                  <Loader2 className="h-8 w-8 text-primary-600 animate-spin" />
                </div>
              )}
            </div>
          </div>

          {/* Le tarif réduit */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
            <div className="p-4 bg-gray-50 border-b">
              <h2 className="text-lg font-semibold">Tarif réduit</h2>
            </div>
            <div className="p-4">
              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={tarifReduit}
                  onChange={basculerTarifReduit}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">
                  Je demande le tarif réduit et je déclare sur l'honneur que les revenus
                  de mon ménage sont inférieurs à {euros(SEUIL_REVENUS_CENTIMES)} net par mois.
                </span>
              </label>

              {tarifReduit && (
                <div className="mt-4 bg-green-50 p-3 rounded-lg flex items-start">
                  <Info size={20} className="text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-700">
                    Déclaration enregistrée. Elle sera conservée avec cette commande, et
                    des justificatifs pourront vous être demandés.
                  </p>
                </div>
              )}
              {/*
                La demande est refaite a CHAQUE commande : « ses conditions
                d'aujourd'hui ne sont pas celles de demain ». Rien n'est
                pre-rempli d'une commande sur l'autre.
              */}
              <p className="mt-3 text-xs text-gray-500">
                Cette demande vaut pour cette commande uniquement.
              </p>
            </div>
          </div>

          {/* Les codes cadeaux */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-4 bg-gray-50 border-b flex items-center">
              <Gift size={18} className="text-primary-600 mr-2" />
              <h2 className="text-lg font-semibold">Code cadeau</h2>
            </div>
            <div className="p-4">
              <form onSubmit={soumettreCode} className="flex gap-2">
                <input
                  type="text"
                  value={saisieCode}
                  onChange={(e) => setSaisieCode(e.target.value)}
                  placeholder="Ex. AUT-2026"
                  className="flex-grow rounded-lg border-gray-300 focus:border-primary-500 focus:ring-primary-500 text-sm"
                />
                <button type="submit" className="btn-outline whitespace-nowrap" disabled={!saisieCode.trim()}>
                  Ajouter
                </button>
              </form>

              {codes.length > 0 && (
                <ul className="mt-4 space-y-2">
                  {codes.map((code) => {
                    const ligne = devis?.lines.find((l) => l.gratuity_code === code);
                    const article = ligne ? parLigne.get(`${ligne.session_id}|${ligne.kid_id}`) : undefined;
                    const fautif = refus?.code === code;
                    return (
                      <li
                        key={code}
                        className={clsx(
                          'flex items-center justify-between rounded-lg px-3 py-2',
                          fautif ? 'bg-red-50 border border-red-200' : 'bg-gray-50',
                        )}
                      >
                        <div className="min-w-0">
                          <span className="inline-flex items-center text-sm font-medium">
                            <Ticket
                              size={14}
                              className={clsx('mr-1.5', fautif ? 'text-red-600' : 'text-primary-600')}
                            />
                            {code}
                          </span>
                          {fautif && (
                            <p className="text-xs text-red-700 mt-0.5">{refus.message}</p>
                          )}
                          {ligne && article && !fautif && (
                            <p className="text-xs text-green-700 mt-0.5 truncate">
                              Offre {article.activityName} pour {article.kidName} — {euros(ligne.gratuity_cents)}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => setCodes(retirerCode(codes, code))}
                          className="text-gray-400 hover:text-red-600 flex-shrink-0 ml-2"
                          aria-label={`Retirer le code ${code}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}

              <p className="mt-3 text-xs text-gray-500">
                Plusieurs codes différents peuvent être cumulés — un par semaine offerte.
                Chaque code rend gratuite la semaine la moins chère parmi celles qu'il
                couvre, et un même code ne compte qu'une fois.
              </p>
            </div>
          </div>
        </div>

        {/* Le récapitulatif */}
        <div className="order-1 md:order-2">
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-4 bg-gray-50 border-b">
              <h2 className="text-lg font-semibold">Récapitulatif de la commande</h2>
            </div>

            <div className="p-4">
              {erreurDevis && (
                <div className="bg-red-50 border border-red-200 p-3 rounded-lg mb-4">
                  <div className="flex items-start">
                    <AlertCircle size={20} className="text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-red-800">
                        {refus ? refus.message : erreurDevis}
                      </p>
                      {refus?.conseil && (
                        <p className="mt-1 text-red-700">{refus.conseil}</p>
                      )}
                    </div>
                  </div>

                  {/*
                    Le geste, la ou le probleme est lu. Sans lui, il fallait
                    faire defiler l'ecran, trouver la liste des codes et
                    deviner lequel retirer.
                  */}
                  {refus?.code && (
                    <button
                      onClick={() => setCodes(retirerCode(codes, refus.code!))}
                      className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-white px-2.5 py-1.5 text-xs font-medium text-red-800 hover:bg-red-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Retirer le code « {refus.code} » et recalculer
                    </button>
                  )}
                </div>
              )}

              {/*
                Le panier reste VISIBLE quand le devis echoue. L'ancien ecran
                ne montrait plus que le message : le parent ne voyait ni ses
                semaines ni ce qu'il devait corriger. Les montants, eux, sont
                masques — un total perime sur un ecran de paiement est pire
                qu'un total absent.
              */}
              {!devis && !chargement && items.length > 0 && (
                <div className="space-y-2 mb-4">
                  {items.map((i) => (
                    <div key={i.id} className="flex justify-between text-sm">
                      <div>
                        <p className="font-medium text-gray-800">{i.activityName}</p>
                        <p className="text-gray-600">Pour {i.kidName}</p>
                        <p className="text-xs text-gray-500">{i.dateRange}</p>
                      </div>
                      <span className="text-gray-400">—</span>
                    </div>
                  ))}
                  <p className="border-t pt-2 text-xs text-gray-500">
                    Les montants réapparaîtront dès que le point ci-dessus sera corrigé.
                    Vos semaines, elles, sont conservées.
                  </p>
                </div>
              )}

              {!devis && chargement && (
                <div className="py-8 flex justify-center">
                  <Loader2 className="h-8 w-8 text-primary-600 animate-spin" />
                </div>
              )}

              {devis && (
                <div className={chargement ? 'opacity-50 transition-opacity' : 'transition-opacity'}>
                  <div className="space-y-3 mb-4">
                    {devis.lines.map((l) => {
                      const article = parLigne.get(`${l.session_id}|${l.kid_id}`);
                      const offerte = l.gratuity_cents > 0;
                      return (
                        <div key={`${l.session_id}-${l.kid_id}`} className="flex justify-between">
                          <div className="flex-grow min-w-0 pr-3">
                            <p className="font-medium">{article?.activityName ?? 'Semaine'}</p>
                            <p className="text-sm text-gray-600">Pour {article?.kidName ?? '—'}</p>
                            <p className="text-xs text-gray-500">{article?.dateRange}</p>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {estReduit(l.price_type) && (
                                <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                                  {libelleTarif(l.price_type)}
                                </span>
                              )}
                              {estLocal(l.price_type) && !estReduit(l.price_type) && (
                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                  {libelleTarif(l.price_type)}
                                </span>
                              )}
                              {offerte && (
                                <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                                  Offerte — {l.gratuity_code}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <span className={offerte ? 'text-gray-400 line-through' : 'font-medium'}>
                              {euros(l.amount_cents)}
                            </span>
                            {offerte && <p className="font-medium text-green-700">0,00 €</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="border-t pt-3 space-y-1.5 text-sm">
                    <div className="flex justify-between text-gray-600">
                      <span>Sous-total</span>
                      <span>{euros(devis.gross_cents)}</span>
                    </div>
                    {devis.gratuity_cents > 0 && (
                      <div className="flex justify-between text-green-700">
                        <span>Codes cadeaux</span>
                        <span>−{euros(devis.gratuity_cents)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold text-base pt-1.5 border-t">
                      <span>Montant facturé</span>
                      <span className="text-primary-600">{euros(devis.amount_cents)}</span>
                    </div>
                    {devis.provision_applicable_cents > 0 && (
                      <div className="flex justify-between text-blue-700">
                        <span>Payé par votre provision</span>
                        <span>−{euros(devis.provision_applicable_cents)}</span>
                      </div>
                    )}
                    {devis.provision_applicable_cents > 0 && (
                      <div className="flex justify-between font-semibold text-base pt-1.5 border-t">
                        <span>Reste à payer</span>
                        <span className="text-primary-600">{euros(devis.remaining_cents)}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 bg-primary-50 p-3 rounded-lg flex items-start">
                    <AlertCircle size={20} className="text-primary-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-primary-700">{phraseDeReste(devis)}</p>
                  </div>
                </div>
              )}

              {/*
                Ce bouton n'ecrit plus rien : il OUVRE la fenetre de paiement.
                Le drapeau ne le concerne donc que pour le neutraliser pendant
                qu'un choix est en vol derriere elle.
              */}
              <button
                onClick={valider}
                disabled={enCours !== null || chargement || !devis}
                className="w-full btn-primary py-3 flex items-center justify-center mt-4 disabled:opacity-50"
              >
                {enCours !== null ? (
                  <span className="flex items-center">
                    <Loader2 className="animate-spin mr-2 h-4 w-4" />
                    Traitement en cours...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <FileText className="h-5 w-5 mr-2" />
                    Valider les inscriptions
                  </span>
                )}
              </button>

              {erreur && (
                <div className="mt-4 bg-red-50 p-3 rounded-lg flex items-start">
                  <AlertCircle size={20} className="text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-700">
                    <p>{erreur}</p>
                    {!completudeProfil(profile).complet && (
                      <Link
                        to="/profile"
                        className="mt-1 inline-flex min-h-[44px] items-center font-medium underline"
                      >
                        Compléter mon profil
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* La déclaration sur l'honneur */}
      <Dialog
        open={modaleDeclaration}
        onClose={() => setModaleDeclaration(false)}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        <div className="flex min-h-screen items-center justify-center p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-auto p-6">
            <Dialog.Title className="text-lg font-medium mb-4">
              Déclaration sur l'honneur
            </Dialog.Title>
            <div className="mb-6">
              <p className="text-gray-700 mb-4">{texteDeclaration()}</p>
              <label className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  checked={declarationCochee}
                  onChange={(e) => setDeclarationCochee(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Je confirme cette déclaration sur l'honneur
                </span>
              </label>
            </div>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setModaleDeclaration(false)} className="btn-outline">
                Annuler
              </button>
              <button
                onClick={confirmerDeclaration}
                disabled={!declarationCochee}
                className="btn-primary disabled:opacity-50"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      </Dialog>

      <ModalePaiement
        isOpen={fenetreOuverte}
        onClose={fermerLaFenetre}
        onPayerMaintenant={() => void payerMaintenant()}
        onPayerPlusTard={() => void payerPlusTard()}
        /*
          L'offre vient du DEVIS fige a l'ouverture, et non d'une commande qui
          n'existe pas encore. C'est le meme `remaining_cents` : `create_order`
          reprend le calcul de `quote_order`, et le montant reellement encaisse
          est de toute facon relu en base par la fonction edge — rien de ce
          chiffre ne voyage jusqu'a Stripe.
        */
        offre={offre}
        enCours={enCours}
        erreur={erreurPaiement}
      />
    </div>
  );
};

export default CheckoutPage;
